import STableLayout, {
  DEFAULT_PAGE_SIZE,
  TableLayoutRef,
} from "@/styles/STableLayout";
import { CommonListParams, CommonStateEnum } from "@/types/common";
import { Supplier, SupplierPrice, SupplierPriceAdd } from "@/types/purchase";
import { App, Button, Flex, Input, Modal, notification, Spin, Tooltip, Upload } from "antd";
import type { UploadProps } from "antd";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useDataList from "@/hooks/useDataList";
import apis, { list } from "@/apis/supplier";
import purchaseApis, { priceList, priceUpdate } from "@/apis/purchase";
import useDataAdd from "@/hooks/useDataAdd";
import useDataUpdate from "@/hooks/useDataUpdate";
import useDataDel from "@/hooks/useDataDel";
import { AnyObject } from "antd/es/_util/type";
import useFrontDelTip from "@/hooks/useFrontDelTip";
import usePriceColumn from "../../hooks/usePriceColumn";
import { ColumnsType } from "antd/lib/table";
import PriceForm from "./PriceForm";
import {
  generateSupplierPriceTemplate,
  parseSupplierPriceFile,
  processSupplierPriceData,
  replaceCurrencyKeys,
} from "./utils";
import { DictContext } from "@/layout/context/DictContext";
import ExportExcelButton, { ExportColumn } from "@/components/ExportExcelButton";
import { dateFormat, priceFormat } from "@/utils/format";
import dayjs from "dayjs";
import { CustomTabPaneContext } from "@/components/TabPane";
import ExportProgressModal from "@/components/ExportProgressModal";
import { productList } from "@/apis/product";
import { divided, noRateAmount } from "@/utils/calc";
import ListFilterBar from "@/components/ListFilterBar";
import useCommittedFilters from "@/components/ListFilterBar/hooks/useCommittedFilters";

interface PriceProps {
  supplier?: Supplier;
}

interface SupplierPriceFilters {
  keyword: string;
}

const INITIAL_SUPPLIER_PRICE_FILTERS: SupplierPriceFilters = {
  keyword: "",
};

const SupplierPriceList: FC<PriceProps> = ({ supplier }) => {
  const { t } = useTranslation();
  const { onOpen } = useContext(CustomTabPaneContext);
  const { modal } = App.useApp();
  const tableRef = useRef<TableLayoutRef | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportLoading, setExportLoading] = useState(false);


  const { currencys } = useContext(DictContext);

  // ✅ 组件卸载时取消请求
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const [userList, total, listLoading, { fetchList, addItem, rmItem, updateItem }] = useDataList<SupplierPrice, [string, CommonListParams?]>({ fetchApi: !!supplier ? apis.supplierPrices : priceList });
  const { loading: addLoading, add } = useDataAdd<[string, SupplierPriceAdd], SupplierPrice>({ api: apis.supplierPriceAdd });
  const { update: updatePrice, loading: updateLoading } = useDataUpdate<[string, SupplierPriceAdd], SupplierPrice>({ api: purchaseApis.priceUpdate });
  const { del } = useDataDel({ api: purchaseApis.priceDel });

  const {
    draftFilters,
    appliedFilters,
    updateDraftFilters,
    commitFilters,
    replaceFilters,
  } = useCommittedFilters<SupplierPriceFilters>(INITIAL_SUPPLIER_PRICE_FILTERS);
  const { keyword } = draftFilters;
  const [addOpen, setAddOpen] = useState<boolean>(false);
  const [detail, setDetail] = useState<SupplierPrice | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);

  const { onConfirm } = useFrontDelTip<void, [string, string]>({ cb: (id, delId) => onDel(id, delId) });
  const columns = usePriceColumn({ onDel: onConfirm, onDetail: setDetail, onOpenTab: onOpen, supplier });

  useEffect(() => {
    const nextFilters = replaceFilters(INITIAL_SUPPLIER_PRICE_FILTERS);
    fetch(0, nextFilters);
    tableRef.current?.resetTablePage();
    // 切换供应商时重置筛选，不能跟随每次 render 生成的 fetch 重复请求。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplier?.id, replaceFilters]);

  const errors: {
    [key in number]: string;
  } = {
    10010: t("common.networkError"),
    71701: t("purchase.priceExisted"),
  };

  const handleImportSupplierPrice: UploadProps['beforeUpload'] = async (file) => {
    setUploading(true);
    try {
      const data = await parseSupplierPriceFile(file, t, !!!supplier);
      if (data.length === 0) return false
      let processedData = processSupplierPriceData(data);
      let productCodes = processedData.map(p => p.productId);
      let productListData = await productList({ params: { strs: productCodes } });
      if ((productListData.body || []).length === 0) {
        notification.error({ message: t('apiError.error26'), description: productCodes[0] })
        return false
      } else {
        for (let i = 0; i < processedData.length; i++) {
          let product = productListData.body.find(p => p.code === processedData[i].productId);
          if (!!!product) {
            notification.error({ message: t('apiError.error26'), description: processedData[i].productId })
            return false
          }
        }
      }
      // 替换 currency 字段为对应的 key
      processedData = replaceCurrencyKeys<SupplierPriceAdd>(processedData, currencys, '1');
      if (!!!supplier) {
        let codes = processedData.filter(p => !!p.supplierCode).map(p => p.supplierCode!);
        if (codes.length === 0) {
          notification.error({
            message: t("purchase.templateWarningIntro"),
            description: (
              <>
                <div>{t("purchase.templateWarning1")}</div>
                <div>{t("purchase.templateWarning2")}</div>
                <div>{t("purchase.templateWarning3")}</div>
                <div>{t("purchase.templateWarning4")}</div>
                <div>{t("purchase.templateWarning5")}</div>
                <div style={{ color: "red" }}>{t("purchase.templateWarning6")}</div>
              </>
            )
          })
          return false
        }
        let supplierData = await list({ strs: codes }, CommonStateEnum.ACTIVE);
        if ((supplierData.body || []).length === 0) {
          notification.error({ message: t('bom.importError'), description: `${t('purchase.supplierNotExisted')}: ${codes[0]}` })
          return false
        }
        for (let i = 0; i < processedData.length; i++) {
          let supplierObj = supplierData.body.find(s => s.code === processedData[i].supplierCode);
          if (!!!supplierObj) {
            notification.error({ message: t('bom.importError'), description: `${t('purchase.supplierNotExisted')}: ${processedData[i].supplierCode}` })
            return false
          }
          else processedData[i].supplierCode = supplierObj.id
        }
      };

      // 批量导入
      const successItems: SupplierPrice[] = [];
      const failedItems: string[] = [];
      for (const item of processedData) {
        let { supplierCode: supplierId, ...addInfo } = item;
        try {
          const res = await add(!!supplier ? supplier.id : (supplierId ?? ''), addInfo);
          successItems.push(res);
        } catch (err: any) {
          if (err === 71701) {
            try {
              let oldData: Array<SupplierPrice> = [];
              oldData = (await apis.supplierPrices(!!supplier ? supplier.id : (supplierId ?? ''), { body: item.productId, }))?.body || []
              if (oldData[0]?.priceId) {
                let newPrice = await priceUpdate(oldData[0].priceId, item);
                successItems.push(newPrice)
              } else failedItems.push(item.productId);
            } catch (error) {
              failedItems.push(item.productId);
            }
          } else failedItems.push(item.productId);
        }
      }

      // 批量更新列表数据
      if (successItems.length > 0) {
        // let adds = successItems.filter(sItem => !!!userList.find(l => l.id === sItem.id))
        // let updates = successItems.filter(sItem => !!userList.find(l => l.id === sItem.id))
        // addItems(successItems);
        fetch(tableRef.current?.offset ?? 0, appliedFilters)
      }

      // 显示导入结果
      if (failedItems.length === 0) {
        notification.success({
          placement: 'top',
          message: t("purchase.importSuccess"),
          description: t("purchase.importSuccessDesc", { count: successItems.length }),
        });
      } else {
        notification.warning({
          placement: 'top',
          message: t("purchase.importCompleted"),
          description: t("purchase.importPartialDesc", {
            success: successItems.length,
            failed: failedItems.length,
            failedList: failedItems.join(', ')
          }),
        });
      }
    } catch (error) {
      notification.error({ placement: 'top', message: t("purchase.fileParseError") });
    } finally {
      setUploading(false);
    }
    return false; // 阻止自动上传
  };

  const handleDownloadTemplate = () => {
    modal.confirm({
      title: t("purchase.templateWarningTitle"),
      content: (
        <>
          <div>{t("purchase.templateWarningIntro")}</div>
          <div>{t("purchase.templateWarning1")}</div>
          <div>{t("purchase.templateWarning2")}</div>
          <div>{t("purchase.templateWarning3")}</div>
          <div>{t("purchase.templateWarning4")}</div>
          <div>{t("purchase.templateWarning5")}</div>
          {!!!supplier && <div>{t("purchase.templateWarning6")}</div>}
        </>
      ),
      okText: t("purchase.iHaveKnown"),
      onOk() {
        generateSupplierPriceTemplate(t, !!!supplier);
      },
      width: 1000,
    })
  };

  const showSupplierExportColumns = !!!supplier; // 是否显示供应商编码/供应商名称列

  // 导出列定义
  const exportColumns: ExportColumn[] = [
    { key: "code", label: t("product.code") },
    { key: "name", label: t("product.name") },
    { key: "brand1", label: t("order.brand") },
    { key: "uom", label: t("product.uom") },
    { key: "brand", label: t("purchase.brand") },
    { key: "mpn", label: t("purchase.mpn") },
    { key: "dcc", label: t("purchase.dcc") },
    { key: "price", label: t("purchase.stPrice") },
    { key: "mpq", label: t("purchase.mpq") },
    { key: "days", label: t("purchase.days") },
    { key: "taxRate", label: t("purchase.taxRate") },
    { key: "unRatePrice", label: t("purchase.unRatePrice") },
    { key: "effectiveAt", label: t("purchase.effectiveAt") },
    { key: "expiredAt", label: t("purchase.expiredAtEcl") },
    { key: "currency", label: t("purchase.currency") },
    { key: "remark", label: t("purchase.remark") },
    ...(showSupplierExportColumns
      ? [
        { key: "supplierCode", label: t("purchase.code") },
        { key: "supplierName", label: t("purchase.name") },
        { key: "supplierShortName", label: t("purchase.shortName") },
      ]
      : []),
    { key: "spareRate", label: t("purchase.spareRate") },
  ];

  // 导出数据映射
  const exportDataMapper = (item: SupplierPrice) => {
    let taxRate = Number(item.taxRate || supplier?.taxRate || item?.supplier?.taxRate || '');
    let unRatePrice = taxRate ? priceFormat(noRateAmount(item.price ?? "", divided(taxRate + '', "100"))) : ""
    let currency = currencys.find(c => c.key === item.currency)?.desc ?? ''
    return {
      [t("product.code")]: item.product?.code ?? "",
      [t("product.name")]: item.product?.name ?? "",
      [t("order.brand")]: item.product?.model ?? "",
      [t("product.uom")]: item.product?.uom?.toLocaleUpperCase() ?? "",
      [t("purchase.brand")]: item.brand ?? "",
      [t("purchase.mpn")]: item?.mpn ?? "",
      [t("purchase.dcc")]: item.dcc ?? "",
      [t("purchase.stPrice")]: priceFormat(item.price ?? ""),
      [t("purchase.mpq")]: item.mpq ?? '',
      [t("purchase.days")]: item.days ?? '',
      [t("purchase.taxRate")]: taxRate ? taxRate + '%' : '',
      [t("purchase.unRatePrice")]: unRatePrice,
      [t("purchase.effectiveAt")]: item.effectiveAt ? dateFormat(dayjs(item.effectiveAt)) : '',
      [t("purchase.expiredAtEcl")]: item.expiredAt ? dateFormat(dayjs(item.expiredAt)) : '',
      [t("purchase.currency")]: currency,
      [t("purchase.remark")]: item.remark ?? "",
      ...(showSupplierExportColumns
        ? {
          [t("purchase.code")]: item?.supplier?.code ?? "",
          [t("purchase.name")]: item?.supplier?.name ?? "",
          [t("purchase.shortName")]: item?.supplier?.shortName ?? "",
        }
        : {}),
      [t("purchase.spareRate")]: item.spareRate ? `${item.spareRate}%` : '',
    }
  };


  // 加载全部数据用于导出
  const loadAllData = async () => {
    if (!!supplier) {
      const res = await apis.supplierPricesFetch(supplier.id, {
        body: appliedFilters.keyword,
      });
      return res.body || [];
    } else {
      setExportLoading(true);
      let allData: SupplierPrice[] = [];
      let hasMore = true;
      let offset = 0;
      let batchSize = 50;

      while (hasMore) {
        const response = await priceList("", {
          body: appliedFilters.keyword,
          pagination: { offset, take: batchSize },
        });
        if (response.body && response.body.length > 0) {
          allData.push(...response.body);
          offset += batchSize;
          const progress = Math.min(
            Math.round(
              (allData.length /
                (response.pagination?.size || allData.length)) *
              100
            ),
            99
          );
          setExportProgress(progress);

          if (response.body.length < batchSize) {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }
      setExportLoading(false);
      return allData
    }
  };

  const handleSearch = () => {
    const nextFilters = commitFilters();
    fetch(0, nextFilters);
    tableRef.current?.resetTablePage();
  };

  const handleClearFilters = () => {
    const nextFilters = replaceFilters(INITIAL_SUPPLIER_PRICE_FILTERS);
    fetch(0, nextFilters);
    tableRef.current?.resetTablePage();
  };

  const headerChildren = (
    <Flex style={{ width: "100%" }} justify="space-between">
      <Flex gap={8}>
        {!!supplier && <Button type="primary" onClick={() => setAddOpen(true)}>
          {t("purchase.addPrice")}
        </Button>}
        <Upload
          accept=".xlsx,.xls"
          beforeUpload={handleImportSupplierPrice}
          showUploadList={false}
          disabled={uploading}
        >
          <Button loading={uploading}>{t("purchase.importSupplierPrice")}</Button>
        </Upload>
        <Button onClick={handleDownloadTemplate}>{t("purchase.downloadPriceTemplate")}</Button>
        <ExportExcelButton
          list={userList}
          columns={exportColumns}
          dataMapper={exportDataMapper}
          fileName={!!supplier ? `${t("purchase.supplierPrice")} ${supplier.code}.xlsx` : `${t("layout.priceList")}.xlsx`}
          isAll={false}
        />
        <ExportExcelButton
          columns={exportColumns}
          dataMapper={exportDataMapper}
          fileName={!!supplier ? `${t("purchase.supplierPrice")} ${supplier.code}.xlsx` : `${t("layout.priceList")}.xlsx`}
          isAll={true}
          loadData={loadAllData}
        />
      </Flex>
      <div style={{ flex: 1, minWidth: 0 }}>
        <ListFilterBar
          onSearch={handleSearch}
          onReset={handleClearFilters}
          disabled={listLoading}
        >
          {({ submit }) => (
            <Tooltip title={t("purchase.bySearchPrice")}>
              <Input
                value={keyword}
                onChange={event =>
                  updateDraftFilters({ keyword: event.target.value.trim() })
                }
                placeholder={t("purchase.bySearchPrice")}
                onPressEnter={submit}
                style={{ width: 200 }}
              />
            </Tooltip>
          )}
        </ListFilterBar>
      </div>
    </Flex>
  );

  const fetch = (offset: number, filters: SupplierPriceFilters) => {
    // ✅ 取消上一次请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // ✅ 创建新的 AbortController
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE

    fetchList(supplier?.id ?? '', {
      body: filters.keyword,
      pagination: { offset, take },
      controller, // ✅ 传递 controller 用于取消请求
    }).catch((err) => {
      // ✅ 忽略取消的请求
      if (err?.name === 'AbortError') {
        return;
      }
    });
  };

  const onDel: (id: string, delId: string) => Promise<void> = (id, delId) => {
    return del(id).then(() => {
      rmItem(delId);
      notification.success({ placement: 'top', message: t("common.rmSuccess") });
    });
  };

  return (
    <Flex vertical style={{ width: "100%", height: "100%" }}>
      <STableLayout
        loading={listLoading}
        dataSource={userList}
        columns={columns as ColumnsType<AnyObject>}
        scrollX={1000}
        headerChildren={headerChildren}
        tableLayoutRef={tableRef}
        total={total}
        handleChange={offset => fetch(offset, appliedFilters)}
      />
      {!!supplier && <PriceForm
        key={"addPrice"}
        open={addOpen}
        supplier={supplier}
        confirmLoading={addLoading}
        handleCancel={() => setAddOpen(false)}
        handleOk={(data) => {
          return new Promise((resolve, reject) => {
            add(supplier.id, { ...data })
              .then((res) => {
                addItem(res);
                setAddOpen(false);
                notification.success({ placement: 'top', message: t('common.addSuccess') })
                resolve();
              })
              .catch((err) => {
                notification.error({
                  placement: 'top',
                  message: t("common.addError"),
                  description: errors[err] ?? t("common.serverError"),
                });
              });
          });
        }}
        leftTitle={t("common.add")}
        type="add"
        id=""
      />}
      <PriceForm
        key={detail?.priceId}
        open={!!detail}
        supplier={supplier}
        confirmLoading={updateLoading}
        handleCancel={() => setDetail(null)}
        handleOk={(data) => {
          return new Promise((resolve, reject) => {
            if (detail) {
              updatePrice(detail.priceId, { ...data })
                .then((res) => {
                  updateItem({ ...res }, 'priceId');
                  setDetail(null);
                  notification.success({ placement: 'top', message: t('common.saveOk') })
                  resolve();
                })
                .catch((err) => {
                  notification.error({
                    placement: 'top',
                    message: t("common.addError"),
                    description: errors[err] ?? t("common.serverError"),
                  });
                  reject();
                });
            }
          });
        }}
        leftTitle={t("common.edit")}
        id={detail?.priceId ?? ""}
        type={"update"}
      />
      {
        uploading && <Modal open={uploading} closable={false}
          title={t("purchase.importSupplierPrice") + "..."} centered
          width={1000} >
          <Flex style={{ width: "100%" }} justify="center" >
            <Spin spinning={uploading} style={{ marginTop: 100, marginBottom: 100 }} size="large" />
          </Flex>
        </Modal>
      }
      <ExportProgressModal visible={exportLoading} progress={exportProgress} />
    </Flex>
  );
};

export default SupplierPriceList;
